// Los Angeles wall-clock time, without a date library. Curb rules are written in local time
// ("Mon-Sat 7am-6pm"), so everything is evaluated on LA calendar days, DST included.

export const TZ = 'America/Los_Angeles'
export const MIN = 60_000
export const HOUR = 60 * MIN
export const DAY = 24 * HOUR

export interface Local {
  ms: number
  date: string // YYYY-MM-DD in LA
  dow: number // 0 = Monday … 6 = Sunday (bit `1 << dow` in rule day masks)
  min: number // minutes since local midnight
}

const parts = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', weekday: 'short',
})
const DOW: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 }

/** LA wall clock straight from Intl: slow (~20 µs), so only used to find the UTC offset. */
export function localIntl(ms: number): Local {
  const p: Record<string, string> = {}
  for (const x of parts.formatToParts(new Date(ms))) p[x.type] = x.value
  const h = Number(p.hour) % 24
  return { ms, date: `${p.year}-${p.month}-${p.day}`, dow: DOW[p.weekday], min: h * 60 + Number(p.minute) }
}

// LA's offset from UTC changes only at DST, and US changes fall on a UTC hour (2am local), so it's
// looked up once per UTC hour and everything else is arithmetic. A plan converts ~300 times; on
// Workers' free plan (10 ms CPU) Intl for each was a third of the budget.
const offsets = new Map<number, number>() // UTC hour → minutes LA is behind UTC
function behind(ms: number): number {
  const hour = Math.floor(ms / HOUR)
  let o = offsets.get(hour)
  if (o === undefined) {
    const l = localIntl(hour * HOUR)
    const [y, m, d] = l.date.split('-').map(Number)
    o = (hour * HOUR - Date.UTC(y, m - 1, d, Math.floor(l.min / 60), l.min % 60)) / MIN
    if (offsets.size > 20_000) offsets.clear()
    offsets.set(hour, o)
  }
  return o
}
const pad = (n: number) => (n < 10 ? '0' + n : String(n))

export function local(ms: number): Local {
  const w = new Date(ms - behind(ms) * MIN) // wall-clock time, read through the UTC getters
  return {
    ms, date: `${w.getUTCFullYear()}-${pad(w.getUTCMonth() + 1)}-${pad(w.getUTCDate())}`,
    dow: (w.getUTCDay() + 6) % 7, min: w.getUTCHours() * 60 + w.getUTCMinutes(),
  }
}

/** UTC ms for an LA wall-clock time. Handles DST by correcting the offset once. */
export function fromLocal(date: string, min: number): number {
  const [y, m, d] = date.split('-').map(Number)
  const guess = Date.UTC(y, m - 1, d, Math.floor(min / 60), min % 60)
  const off1 = offsetMin(guess)
  const t = guess + off1 * MIN
  const off2 = offsetMin(t)
  return off2 === off1 ? t : guess + off2 * MIN
}

// Minutes LA is behind UTC at this instant (420 in summer, 480 in winter).
function offsetMin(ms: number): number {
  const l = local(ms)
  const [y, m, d] = l.date.split('-').map(Number)
  const asUtc = Date.UTC(y, m - 1, d, Math.floor(l.min / 60), l.min % 60)
  return Math.round((Math.floor(ms / MIN) * MIN - asUtc) / MIN)
}

export function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}

// ---------------------------------------------------------------- holidays
// Ported from ~/git/la-parking/lib/holidays.py (the calendar the enforcement model was trained on).

export type HolidayType = 'federal' | 'black_friday' | 'pre_thanksgiving' | 'christmas_eve' | 'nye'

const iso = (y: number, m: number, d: number) => new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10)
const wd = (y: number, m: number, d: number) => (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7

function nth(y: number, m: number, weekday: number, n: number): string {
  const first = 1 + ((weekday - wd(y, m, 1) + 7) % 7)
  return iso(y, m, first + 7 * (n - 1))
}
function last(y: number, m: number, weekday: number): string {
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return iso(y, m, days - ((wd(y, m, days) - weekday + 7) % 7))
}
function observed(y: number, m: number, d: number): string {
  const w = wd(y, m, d)
  return w === 5 ? iso(y, m, d - 1) : w === 6 ? iso(y, m, d + 1) : iso(y, m, d)
}

const holidayCache = new Map<number, Map<string, HolidayType>>()
function holidaysOf(y: number): Map<string, HolidayType> {
  let h = holidayCache.get(y)
  if (h) return h
  h = new Map()
  for (const [m, d] of [[1, 1], [7, 4], [11, 11], [12, 25], [6, 19]]) h.set(observed(y, m, d), 'federal')
  // A Saturday New Year's Day is observed on Friday Dec 31, which belongs to this year's map.
  const nextNewYear = observed(y + 1, 1, 1)
  if (nextNewYear.startsWith(`${y}-`)) h.set(nextNewYear, 'federal')
  h.set(nth(y, 1, 0, 3), 'federal') // MLK
  h.set(nth(y, 2, 0, 3), 'federal') // Presidents'
  h.set(last(y, 5, 0), 'federal') // Memorial
  h.set(nth(y, 9, 0, 1), 'federal') // Labor
  h.set(nth(y, 10, 0, 2), 'federal') // Columbus / Indigenous Peoples'
  const tg = nth(y, 11, 3, 4)
  h.set(tg, 'federal')
  h.set(addDays(tg, 1), 'black_friday')
  h.set(addDays(tg, -1), 'pre_thanksgiving')
  // The eves are lighter days, but never in place of an observed federal holiday on the same date.
  if (wd(y, 12, 24) < 5 && !h.has(iso(y, 12, 24))) h.set(iso(y, 12, 24), 'christmas_eve')
  if (wd(y, 12, 31) < 5 && !h.has(iso(y, 12, 31))) h.set(iso(y, 12, 31), 'nye')
  holidayCache.set(y, h)
  return h
}

export function holidayType(date: string): HolidayType | null {
  return holidaysOf(Number(date.slice(0, 4))).get(date) ?? null
}

/** Days LADOT doesn't run meters or street sweeping (the data shows ~85% less ticketing). */
export function isMeterHoliday(date: string): boolean {
  const t = holidayType(date)
  return t === 'federal' || t === 'black_friday'
}


// ---------------------------------------------------------------- formatting

const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** "7am", "6:30pm", "12am" */
export function clock(min: number): string {
  min = ((min % 1440) + 1440) % 1440
  const h = Math.floor(min / 60), m = min % 60
  const ap = h < 12 ? 'am' : 'pm'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return m ? `${h12}:${String(m).padStart(2, '0')}${ap}` : `${h12}${ap}`
}

/** "6pm" today, "Tue 8am" later this week, "Oct 29 8am" beyond that. `ref` is "now". */
export function when(ms: number, ref: number): string {
  const a = local(ms), b = local(ref)
  if (a.date === b.date) return clock(a.min)
  const days = Math.round((Date.parse(a.date) - Date.parse(b.date)) / DAY)
  if (days === 1 && a.min < 12 * 60 && b.min >= 17 * 60) return clock(a.min) // overnight: "until 7am"
  if (days === 1) return `tomorrow ${clock(a.min)}`
  if (days > 1 && days < 7) return `${DAY_SHORT[a.dow]} ${clock(a.min)}`
  const [, m, d] = a.date.split('-').map(Number)
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]} ${d} ${clock(a.min)}`
}

/** "45 min", "2 hr", "2 hr 30 min", "3 days" */
export function span(ms: number): string {
  const m = Math.round(ms / MIN)
  if (m < 60) return `${m} min`
  if (m >= 48 * 60) return `${Math.round(m / 1440)} days`
  const h = Math.floor(m / 60), r = m % 60
  return r ? `${h} hr ${r} min` : `${h} hr`
}

export function daysLabel(mask: number): string {
  if (mask === 127) return 'Daily'
  if (mask === 31) return 'Mon–Fri'
  if (mask === 96) return 'Sat–Sun'
  const runs: string[] = []
  let i = 0
  while (i < 7) {
    if (!(mask & (1 << i))) { i++; continue }
    let j = i
    while (j + 1 < 7 && mask & (1 << (j + 1))) j++
    runs.push(j === i ? DAY_SHORT[i] : j === i + 1 ? `${DAY_SHORT[i]}, ${DAY_SHORT[j]}` : `${DAY_SHORT[i]}–${DAY_SHORT[j]}`)
    i = j + 1
  }
  return runs.join(', ')
}

export function timesLabel(start: number, end: number): string {
  if (start === 0 && end === 1440) return 'all day'
  // Midnight is spelled out: "8am–12am" shortened to "8–12am" read like four hours in the morning.
  if (start % 1440 === 0 || end % 1440 === 0) return `${start % 1440 === 0 ? 'midnight' : clock(start)}–${end % 1440 === 0 ? 'midnight' : clock(end)}`
  const s = clock(start), e = clock(end)
  // "8–10am" when both share am/pm and are on the hour
  if (s.slice(-2) === e.slice(-2) && !s.includes(':') && !e.includes(':') && start < end) return `${s.slice(0, -2)}–${e}`
  return `${s}–${e}`
}

/**
 * When a "move your car" reminder fires: 15 minutes before. For morning street cleaning, 8pm the
 * night before instead (who moves their car at 7:45am?), as long as that's still ahead.
 */
export function remindAt(moveBy: number, night: boolean, now = Date.now()): number {
  if (night) {
    const eve = fromLocal(addDays(local(moveBy).date, -1), 20 * 60)
    if (eve > now + 10 * MIN && moveBy - eve <= 15 * HOUR) return eve
  }
  return Math.max(now + MIN, moveBy - 15 * MIN)
}

/** Street cleaning before 11am: the reminder that matters is the evening before (remindAt). */
export const nightBefore = (moveBy: number, why: string | undefined) => /street cleaning/i.test(why ?? '') && local(moveBy).min < 11 * 60
